import { useState } from "react";
import { upsertSkippedUserProfile } from "../../lib/userProfileDefaults";

export default function LegalAgreementModal({ userProfile, onSubmit, userId }) {
  const [legal, setLegal] = useState(false);
  const [ageAndRisk, setAgeAndRisk] = useState(false);
  const [loading, setLoading] = useState(false);

  const canSubmit = legal && ageAndRisk;
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      await delay(1000);
      const updatedProfile = {
        ...userProfile,
        terms_privacy_accepted: legal,
        nda_accepted: legal,
        age_confirmed: ageAndRisk,
        legal_accepted_at: new Date().toISOString(),
        user_id: userId
      };
      await upsertSkippedUserProfile(userProfile.user_id, updatedProfile);
      onSubmit(updatedProfile);
    } catch (err) {
      console.error("Error submitting legal agreement:", err);
    } finally {
      setLoading(false);
    }
  };

  const linkClass = "font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800 transition-colors";

  const labelClass = "flex items-start gap-2.5 rounded-lg border border-gray-200 p-2.5 text-xs leading-relaxed text-gray-600 cursor-pointer transition-colors hover:bg-gray-50 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50/50";

  const checkboxClass = "mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-pointer";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm [font-family:'DM_Sans',sans-serif]">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">
          Before You Continue
        </h2>
        <p className="mb-5 text-xs text-gray-500">
          Please review and accept the following to proceed.
        </p>

        <div className="space-y-2.5">
          <label className={labelClass}>
            <input
              type="checkbox"
              checked={legal}
              onChange={(e) => setLegal(e.target.checked)}
              className={checkboxClass}
            />
            <span>
              I have read and agree to the{" "}
              <a
                href="http://goneuroquest.com/access-agreement"
                target="_blank"
                rel="noreferrer"
                className={linkClass}
              >
                Demo &amp; Evaluation Access Agreement
              </a>
              ,{" "}
              <a
                href="https://www.goneuroquest.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className={linkClass}
              >
                Privacy Policy
              </a>
              , and{" "}
              <a
                href="https://www.goneuroquest.com/nda"
                target="_blank"
                rel="noreferrer"
                className={linkClass}
              >
                Non-Disclosure Agreement
              </a>
              .
            </span>
          </label>

          <label className={labelClass}>
            <input
              type="checkbox"
              checked={ageAndRisk}
              onChange={(e) => setAgeAndRisk(e.target.checked)}
              className={checkboxClass}
            />
            <span>
              I&apos;m 18 or older and understand our data security setup
              isn&apos;t finalized. I&apos;ve been advised not to enter
              sensitive information.
            </span>
          </label>
        </div>

        <p className="mt-3 text-[11px] leading-relaxed text-gray-400">
          This is an early demo, not medical advice or a diagnosis.
        </p>

        <button
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-gray-400 px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
        >
          {loading && (
            <svg
              className="h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
          )}
          {loading ? "Submitting…" : "Submit"}
        </button>
      </div>
    </div>
  );
}